import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import PhoneNumberInput from '../../components/PhoneNumberInput';

interface ProfileFormData {
  firstName: string;
  lastName: string;
  email: string;
  websiteUrl: string;
  phoneNumber: string;
  company: string;
}

const Profile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<ProfileFormData>({
    firstName: '',
    lastName: '',
    email: '',
    websiteUrl: '',
    phoneNumber: '',
    company: ''
  });

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        websiteUrl: user.websiteUrl || '',
        phoneNumber: user.phoneNumber || '',
        company: user.company || ''
      });
    }
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhoneChange = (phoneNumber: string) => {
    setFormData(prev => ({
      ...prev,
      phoneNumber
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const response = await api.put('/auth/profile', formData);
      updateUser(response.data.user);
      toast.success('Profile updated successfully!');
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-lg-8 mx-auto">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-1">Profile Settings</h2>
              <p className="text-muted mb-0">Manage your account information and preferences</p>
            </div>
          </div>

          {/* Profile Form */}
          <div className="card">
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  {/* Personal Information */}
                  <div className="col-md-6">
                    <h5 className="mb-3">Personal Information</h5>
                    
                    <div className="mb-3">
                      <label className="form-label fw-semibold">First Name *</label>
                      <input
                        type="text"
                        className="form-control form-control-lg"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        style={{ fontSize: '1rem' }}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">Last Name *</label>
                      <input
                        type="text"
                        className="form-control form-control-lg"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                        style={{ fontSize: '1rem' }}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">Email Address *</label>
                      <input
                        type="email"
                        className="form-control form-control-lg"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        disabled
                        style={{ fontSize: '1rem' }}
                      />
                      <div className="form-text">Email cannot be changed</div>
                    </div>
                  </div>

                  {/* Business Information */}
                  <div className="col-md-6">
                    <h5 className="mb-3">Business Information</h5>
                    
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Website URL *</label>
                      <input
                        type="url"
                        className="form-control form-control-lg"
                        name="websiteUrl"
                        value={formData.websiteUrl}
                        onChange={handleInputChange}
                        placeholder="https://example.com"
                        required
                        style={{ fontSize: '1rem' }}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">Phone Number</label>
                      <PhoneNumberInput
                        value={formData.phoneNumber}
                        onChange={handlePhoneChange}
                        placeholder="Enter phone number"
                        className="form-control-lg"
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">Company</label>
                      <input
                        type="text"
                        className="form-control form-control-lg"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        placeholder="Your Company Name"
                        style={{ fontSize: '1rem' }}
                      />
                    </div>
                  </div>
                </div>

                {/* Account Information */}
                <div className="mt-4">
                  <h5 className="mb-3">Account Information</h5>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Account Status</label>
                        <div className="form-control-plaintext">
                          <span className={`badge ${user?.subscription?.status === 'active' ? 'bg-success' : 'bg-warning'}`}>
                            {user?.subscription?.status === 'active' ? 'Active' : 'Free'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Plan</label>
                        <div className="form-control-plaintext">
                          <span className="text-capitalize">{user?.subscription?.plan || 'Free'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Pages Used</label>
                        <div className="form-control-plaintext">
                          {user?.subscription?.pagesUsed || 0} / {user?.subscription?.pageLimit || 100}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Member Since</label>
                        <div className="form-control-plaintext">
                          {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="d-flex justify-content-end gap-2 mt-4">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      if (user) {
                        setFormData({
                          firstName: user.firstName || '',
                          lastName: user.lastName || '',
                          email: user.email || '',
                          websiteUrl: user.websiteUrl || '',
                          phoneNumber: user.phoneNumber || '',
                          company: user.company || ''
                        });
                      }
                    }}
                  >
                    Reset
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Security Section */}
          <div className="card mt-4">
            <div className="card-body">
              <h5 className="mb-3">Security</h5>
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Email Verification</label>
                    <div className="form-control-plaintext">
                      <span className={`badge ${user?.isEmailVerified ? 'bg-success' : 'bg-warning'}`}>
                        {user?.isEmailVerified ? 'Verified' : 'Not Verified'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Last Login</label>
                    <div className="form-control-plaintext">
                      {user?.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'N/A'}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-3">
                <button className="btn btn-outline-primary btn-sm me-2">
                  <i className="fas fa-key me-2"></i>
                  Change Password
                </button>
                <button className="btn btn-outline-secondary btn-sm">
                  <i className="fas fa-shield-alt me-2"></i>
                  Two-Factor Authentication
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
